<template>
  <div
    data-component="base-date-time-range-picker"
    class="flex w-full md:inline-flex flex-col items-center overflow-auto"
    @click="$emit('click', $event)"
  >
    <base-date-time-range-picker-header
      v-if="isSingleCalendarPicker || isMobile"
      :from-label="getDateLabel.from"
      :to-label="getDateLabel.to"
      :active-range-key="activeRangeKey"
      @select-range-key="onRangeKeySelect"
    />

    <div class="flex gap-sm w-full md:w-auto">
      <template v-if="showPickerFrom">
        <div class="flex flex-col gap-xs grow mb-sm">
          <or-date-time-picker-month-select
            v-model="initialValueFrom"
            :locale="LOCALE"
            class="!pb-sm"
            @update:modelValue="activeRangeKey = 'from'"
          />

          <or-date-time-picker-date-select
            :model-value="activeDateModel"
            :locale="LOCALE"
            :format="FORMAT"
            :initial-value="initialValueFrom"
            :range-start="dateFrom || initialValueFrom"
            :range-end="dateTo || initialValueTo"
            :date-filter="dateFromFilter"
            class="!py-sm"
            @update:modelValue="onUpdateDateFrom"
          />

          <base-time-picker
            :hours="time.hoursFrom"
            :minutes="time.minutesFrom"
            :error="isTimeError ? $t('HITL.agent-ui.search-sessions.date-range-picker.time-error') : ''"
            @time-change="onTimeChange($event, 'from')"
          />
        </div>
      </template>

      <template v-if="showPickerTo">
        <div class="flex flex-col gap-xs grow mb-sm">
          <or-date-time-picker-month-select
            v-model="initialValueTo"
            :locale="LOCALE"
            class="!pb-sm"
            @update:modelValue="activeRangeKey = 'to'"
          />

          <or-date-time-picker-date-select
            :model-value="activeDateModel"
            :locale="LOCALE"
            :format="FORMAT"
            :initial-value="initialValueTo"
            :range-start="dateFrom || initialValueFrom"
            :range-end="dateTo || initialValueTo"
            :date-filter="dateToFilter"
            class="!py-sm"
            @update:modelValue="onUpdateDateTo"
          />

          <base-time-picker
            :hours="time.hoursTo"
            :minutes="time.minutesTo"
            :error="isTimeError ? $t('HITL.agent-ui.search-sessions.date-range-picker.time-error') : ''"
            @time-change="onTimeChange($event, 'to')"
          />
        </div>
      </template>
    </div>

    <div class="flex gap-md justify-between w-full p-sm border-t-1 border-solid border-outline-variant dark:border-outline-variant-dark">
      <or-button
        class="grow"
        :variant="ButtonVariant.Text"
        @click="onCancel"
      >
        {{ $t('HITL.common.actions.cancel') }}
      </or-button>

      <or-button
        :variant="ButtonVariant.Outlined"
        class="grow"
        @click="onDateTimeConfirm"
      >
        {{ $t('HITL.agent-ui.search-sessions.date-range-picker.done-button') }}
      </or-button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from 'vue-demi';
import {
  OrButtonV3 as OrButton,
  ButtonVariant,
  OrDateTimePickerMonthSelect,
  OrDateTimePickerDateSelect,
  useResponsive,
} from '@onereach/ui-components-25';

import { getUTCDate, notEmpty, getHumanFriendlyDate, getHours, getMinutes } from './helpers';
import { WritableQueryDateValue, QueryDateRangeKey } from '../../types';
import BaseTimePicker from './BaseTimePicker.vue';
import BaseDateTimeRangePickerHeader from './BaseDateTimeRangePickerHeader.vue';

type RangeDirection = 'forward' | 'backward';

const LOCALE = 'en-US';
const FORMAT = 'short';

/** Accepts strings or date values, emits back a date value */
export default defineComponent({
  name: 'BaseDateTimeRangePicker',
  components: {
    OrButton,
    OrDateTimePickerMonthSelect,
    OrDateTimePickerDateSelect,
    BaseDateTimeRangePickerHeader,
    BaseTimePicker,
  },
  props: {
    datetime: {
      type: Object as PropType<WritableQueryDateValue>,
      default: () => ({ from: '', to: '' }),
    },
    initialRangeDirection: {
      type: String as PropType<RangeDirection>,
      default: 'forward',
    },
    maxDateFilter: {
      type: Function as PropType<(range: WritableQueryDateValue) => (date: Date) => boolean>,
      default: () => () => true,
    },
    minDateFilter: {
      type: Function as PropType<(range: WritableQueryDateValue) => (date: Date) => boolean>,
      default: () => () => true,
    },
    isSingleCalendarPicker: Boolean,
  },

  emits: ['datetime-change', 'cancel'],

  setup(props, ctx) {
    const { isMobile, isDesktop } = useResponsive();

    const activeRangeKey = ref<QueryDateRangeKey>('from');

    const isForward = props.initialRangeDirection === 'forward';
    const defaultFrom = isForward ? getUTCDate(new Date()) : getUTCDate(new Date(), -1);
    const defaultTo = isForward ? getUTCDate(new Date(), 1) : getUTCDate(new Date());
    const initialValueFrom = ref(notEmpty(props.datetime.from) ? getUTCDate(props.datetime.from as Date) : defaultFrom);
    const initialValueTo = ref(notEmpty(props.datetime.to) ? getUTCDate(props.datetime.to as Date) : defaultTo);
    const date = ref({
      from: initialValueFrom.value,
      to: initialValueTo.value,
    });
    /*
      NOTE: need to keep track of time separately from date
      so that OrDateTimePickerDateSelect could pick up selected date,
      since it matches zero-time UTC value.
    */
    const hoursFrom = getHours(props.datetime.from);
    const hoursTo = getHours(props.datetime.to);
    const minutesFrom = getMinutes(props.datetime.from);
    const minutesTo = getMinutes(props.datetime.to);
    const time = ref({
      hoursFrom,
      hoursTo,
      minutesFrom,
      minutesTo,
    });

    const currentDateTime = computed<{ from: Date; to: Date; }>(() => {
      const from = new Date(date.value.from);
      (from as Date).setHours(time.value.hoursFrom);
      (from as Date).setMinutes(time.value.minutesFrom);
      const to = new Date(date.value.to);
      (to as Date).setHours(time.value.hoursTo);
      (to as Date).setMinutes(time.value.minutesTo);
      return {
        from,
        to,
      };
    });

    const isTimeError = computed<boolean>(() => {
      return currentDateTime.value.from > currentDateTime.value.to;
    });

    const dateFrom = computed({
      get() {
        return date.value.from;
      },
      set(value: Date) {
        date.value = {
          from: value,
          to: date.value.to,
        };
      },
    });

    const dateTo = computed({
      get() {
        return date.value.to;
      },
      set(value: Date) {
        date.value = {
          from: date.value.from,
          to: value,
        };
      },
    });

    const activeDateModel = computed({
      get() {
        if (activeRangeKey.value === 'from') {
          return dateFrom.value;
        } else {
          return dateTo.value;
        }
      },
      set(value: Date) {
        if (activeRangeKey.value === 'from') {
          dateFrom.value = value;
        } else dateTo.value = value;
      },
    });

    const getDateLabel = computed<{ from: string; to: string; }>(() => {
      if (date.value.from && date.value.to) {
        const from = getHumanFriendlyDate(date.value.from, FORMAT, LOCALE);
        const to = getHumanFriendlyDate(date.value.to, FORMAT, LOCALE);
        return { from, to };
      } else return {
        from: '--',
        to: '--',
      };
    });

    const showPickerFrom = computed(() => {
      if (activeRangeKey.value === 'from') return true;
      return !props.isSingleCalendarPicker && isDesktop.value;
    });

    const showPickerTo = computed(() => {
      if (activeRangeKey.value === 'to') return true;
      return !props.isSingleCalendarPicker && isDesktop.value;
    });

    const dateFromFilter = computed(() => {
      return props.minDateFilter(date.value);
    });

    const dateToFilter = computed(() => {
      return props.maxDateFilter(date.value);
    });

    watch(date, (value) => {
      if (value && value.from && value.to && Number(value.from) > Number(value.to)) {
        // swap from / to values for date and initialValue
        date.value = { from: value.to, to: value.from };
        const originalFrom = initialValueFrom.value;
        initialValueFrom.value = initialValueTo.value;
        initialValueTo.value = originalFrom;

        toggleActiveRangeKey();
      }
    });

    function toggleActiveRangeKey() {
      if (activeRangeKey.value === 'from') activeRangeKey.value = 'to';
      else activeRangeKey.value = 'from';
    }

    function onTimeChange(options: { hours: number; minutes: number; }, range: QueryDateRangeKey) {
      const { hours, minutes } = options;
      if (range === 'from') {
        time.value = {
          ...time.value,
          hoursFrom: hours,
          minutesFrom: minutes,
        };
      } else {
        time.value = {
          ...time.value,
          hoursTo: hours,
          minutesTo: minutes,
        };
      }
    }

    function onUpdateDateFrom(date: Date) {
      activeRangeKey.value = 'from';
      activeDateModel.value = date;
    }

    function onUpdateDateTo(date: Date) {
      activeRangeKey.value = 'to';
      activeDateModel.value = date;
    }

    function onRangeKeySelect(key: QueryDateRangeKey) {
      activeRangeKey.value = key;
    }

    function onDateTimeConfirm() {
      ctx.emit('datetime-change', currentDateTime.value);
    }

    function onCancel() {
      ctx.emit('cancel');
    }

    return {
      isMobile,
      isDesktop,
      showPickerFrom,
      showPickerTo,
      activeRangeKey,
      initialValueFrom,
      initialValueTo,
      date,
      dateFrom,
      dateTo,
      activeDateModel,
      time,
      isTimeError,
      onRangeKeySelect,
      onTimeChange,
      onUpdateDateFrom,
      onUpdateDateTo,
      onDateTimeConfirm,
      onCancel,
      getDateLabel,
      dateFromFilter,
      dateToFilter,
      LOCALE,
      FORMAT,
      ButtonVariant,
    };
  },
});
</script>
